import { getProductByProductIdVariantIdFromProducts, getTotalUnitQuantityByProductAndDelwins } from '@app/logic/Orders';
import { applyPrices } from '@app/logic/price';
import { getAllOrderedVariants, getOrderedProducts, getRelatedProducts } from '@app/logic/products';
import { isDefined } from '@app/utils/is';
import { DeliveryWindow, Order, Product } from '@typings';
import { groupBy, isEmpty } from 'ramda';
import React, { Fragment } from 'react';

import { CheckoutProduct } from './CheckoutProduct';

interface Props {
  deliveryWindowIds: DeliveryWindow.Id[];
  order: Order.Open;
  products: never[] | Record<Product.Id, Product.Full>;
  variants: Order.Product[];
  isExpired?: boolean;
}

export const CheckoutProducts = (props: Props) => {
  const { order, variants, deliveryWindowIds } = props;
  const products = React.useMemo(() => (Array.isArray(props.products) ? {} : props.products), [props.products]);
  const groupedVariants = React.useMemo(() => groupBy((variant: Order.Product) => variant.product)(Object.values(variants)), [variants]);

  const filteredProducts = React.useMemo(() => {
    return Object.values(groupedVariants)
      .map(variantGroup => {
        const [firstVariant] = variantGroup;
        if (!isDefined(firstVariant)) {
          return null;
        }

        const productData = products[firstVariant.product];

        if (!isDefined(productData)) {
          return null;
        }

        const productInfo = applyPrices(order.products)(productData);
        const unitsCount = getTotalUnitQuantityByProductAndDelwins(firstVariant.product, deliveryWindowIds, order.products);

        if (unitsCount === 0) {
          return null;
        }
        const variantsData = getAllOrderedVariants(productData);

        const orderedProduct = variantsData.reduce((acc, variant) => {
          if (isDefined(acc)) return acc;

          return getProductByProductIdVariantIdFromProducts(variant.product, variant.variant, getOrderedProducts(order));
        }, undefined);

        if (!isDefined(orderedProduct)) {
          return null;
        }

        const fullProductVariants = [productInfo, ...getRelatedProducts(productInfo)];

        if (isEmpty(fullProductVariants)) {
          return null;
        }

        return productInfo;
      })
      .filter(isDefined);
  }, [deliveryWindowIds, groupedVariants, order, products]);

  if (Array.isArray(products)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-2">
      {filteredProducts.map((productInfo, index) => (
        <Fragment key={productInfo.product}>
          {index !== 0 && <div className="mx-4 h-[1px] bg-neutral-90" />}
          <CheckoutProduct deliveryWindowIds={deliveryWindowIds} productInfo={productInfo} />
        </Fragment>
      ))}
    </div>
  );
};
